import { Note, NoteSet, Accidental } from './note';
import { ChordNotationType } from '../contexts/SettingsContext';

const chordIntervals: { [key: string]: (number | undefined)[] } = {
  // Triads
  "": [4, 7, undefined, undefined, undefined, undefined],
  "aug": [4, 8, undefined, undefined, undefined, undefined],
  "min": [3, 7, undefined, undefined, undefined, undefined],
  "dim": [3, 6, undefined, undefined, undefined, undefined],
  "sus2": [2, 7, undefined, undefined, undefined, undefined],
  "sus4": [5, 7, undefined, undefined, undefined, undefined],
  "no3d": [undefined, 7, undefined, undefined, undefined, undefined],
  // Seventh
  "maj7": [4, 7, 11, undefined, undefined, undefined],
  "augmaj7": [4, 8, 11, undefined, undefined, undefined],
  "min7": [3, 7, 10, undefined, undefined, undefined],
  "minmaj7": [3, 7, 11, undefined, undefined, undefined],
  "7": [4, 7, 10, undefined, undefined, undefined],
  "7sus2": [2, 7, 10, undefined, undefined, undefined],
  "7sus4": [5, 7, 10, undefined, undefined, undefined],
  "dim7": [3, 6, 10, undefined, undefined, undefined], // half diminished
  "dimb7": [3, 6, 9, undefined, undefined, undefined], // fully diminished
  "maj7sus2": [2, 7, 11, undefined, undefined, undefined],
  "maj7sus4": [5, 7, 11, undefined, undefined, undefined],
  // Triads with tensions
  "add9": [4, 7, undefined, 14, undefined, undefined],
  "add11": [4, 7, undefined, undefined, 17, undefined],
  "add13": [4, 7, undefined, undefined, undefined, 21],
  "minadd9": [3, 7, undefined, 14, undefined, undefined],
  "minadd11": [3, 7, undefined, undefined, 17, undefined],
  "minadd13": [3, 7, undefined, undefined, undefined, 21],
  "dimadd11": [3, 6, undefined, undefined, 17, undefined],
  "dimadd13": [3, 6, undefined, undefined, undefined, 21],
  // Ninths
  "9": [4, 7, 10, 14, undefined, undefined],
  "7b9": [4, 7, 10, 13, undefined, undefined],
  "b7b9": [4, 7, 9, 13, undefined, undefined],
  "b9": [4, 7, 10, 13, undefined, undefined],
  "min9": [3, 7, 10, 14, undefined, undefined],
  "minb9": [3, 7, 10, 13, undefined, undefined],
  "minmaj9": [3, 7, 11, 14, undefined, undefined],
  "dim9": [3, 6, 10, 14, undefined, undefined],
  "dimb9": [3, 6, 10, 13, undefined, undefined],
  "majs9": [4, 7, 11, 15, undefined, undefined],
  "maj9": [4, 7, 11, 14, undefined, undefined],
  "augmaj9": [4, 8, 11, 14, undefined, undefined],
  // Elevenths
  "11": [4, 7, 10, 14, 17, undefined],
  "11b9": [4, 7, 10, 13, 17, undefined],
  "11s": [4, 7, 10, 14, 18, undefined],
  "maj11": [4, 7, 11, 14, 17, undefined],
  "min11": [3, 7, 10, 14, 17, undefined],
  "minmaj11": [3, 7, 11, 14, 17, undefined],
  "augmaj11": [4, 8, 11, 14, 17, undefined],
  "dim11": [3, 6, 10, 14, 17, undefined],
  "dim11b9": [3, 6, 10, 13, 17, undefined],
  "b11b9": [4, 7, 10, 13, 16, undefined],
  "maj911s": [4, 7, 11, 14, 18, undefined],
  "majs911s": [4, 7, 11, 15, 18, undefined],
  // Thirteenths
  "13": [4, 7, 10, 14, 17, 21],
  "13b": [4, 7, 10, 14, 17, 20],
  "13b9": [4, 7, 10, 13, 17, 21],
  "dim13b9": [3, 6, 10, 13, 17, 21],
  "maj13": [4, 7, 11, 14, 17, 21],
  "maj1311s": [4, 7, 11, 14, 18, 21],
  "min1113b": [3, 7, 10, 14, 17, 20],
  "min13": [3, 7, 10, 14, 17, 21],
  "minmaj13": [3, 7, 11, 14, 17, 21],
};

const chordSymbols: { [key: string]: string } = {
  // Triads
  "": "",
  "aug": "aug",
  "min": "min",
  "dim": "dim",
  "sus2": "<sup>sus2</sup>",
  "sus4": "<sup>sus4</sup>",
  "no3d": "(no3)",
  // Seventh
  "maj7": "maj<sup>7</sup>",
  "augmaj7": "aug<sup>maj7</sup>",
  "min7": "min<sup>7</sup>",
  "minmaj7": "min<sup>maj7</sup>",
  "7": "<sup>7</sup>",
  "7sus2": "<sup>7sus2</sup>",
  "7sus4": "<sup>7sus4</sup>",
  "dim7": "min<sup>7♭5</sup>",
  "dimb7": "dim<sup>7</sup>",
  "maj7sus2": "maj<sup>7sus2</sup>",
  "maj7sus4": "maj<sup>7sus4</sup>",
  // Triads with tensions
  "add9": "<sup>add9</sup>",
  "add11": "<sup>add11</sup>",
  "add13": "<sup>6</sup>",
  "minadd9": "min<sup>add9</sup>",
  "minadd11": "min<sup>add11</sup>",
  "minadd13": "min<sup>6</sup>",
  "dimadd11": "dim<sup>add11</sup>",
  "dimadd13": "dim<sup>6</sup>",
  // Ninths
  "9": "<sup>9</sup>",
  "7b9": "<sup>7(♭9)</sup>",
  "b7b9": "<sup>6(♭9)</sup>",
  "b9": "<sup>7(♭9)</sup>",
  "min9": "min<sup>9</sup>",
  "minb9": "min<sup>7(♭9)</sup>",
  "minmaj9": "min<sup>maj9</sup>",
  "dim9": "min<sup>9(♭5)</sup>",
  "dimb9": "min<sup>7(♭5♭9)</sup>",
  "majs9": "maj<sup>7(♯9)</sup>",
  "maj9": "maj<sup>9</sup>",
  "augmaj9": "aug<sup>maj9</sup>",
  // Elevenths
  "11": "<sup>11</sup>",
  "11b9": "<sup>11(♭9)</sup>",
  "11s": "<sup>♯11</sup>",
  "maj11": "maj<sup>11</sup>",
  "min11": "min<sup>11</sup>",
  "minmaj11": "min<sup>maj11</sup>",
  "augmaj11": "aug<sup>maj11</sup>",
  "dim11": "min<sup>11(♭5)</sup>",
  "dim11b9": "min<sup>11(♭5♭9)</sup>",
  "b11b9": "<sup>♭11(♭9)</sup>",
  "maj911s": "maj<sup>9(♯11)</sup>",
  "majs911s": "maj<sup>7(♯9♯11)</sup>",
  // Thirteenths
  "13": "<sup>13</sup>",
  "13b": "<sup>♭13</sup>",
  "13b9": "<sup>13(♭9)</sup>",
  "dim13b9": "min<sup>13(♭5♭9)</sup>",
  "maj13": "maj<sup>13</sup>",
  "maj1311s": "maj<sup>13(♯11)</sup>",
  "min1113b": "min<sup>11(♭13)</sup>",
  "min13": "min<sup>13</sup>",
  "minmaj13": "min<sup>maj13</sup>",
};

const chordSymbolsShort: { [key: string]: string } = {
  // Triads
  "": "",
  "aug": "+",
  "min": "-",
  "dim": "°",
  "sus2": "<sup>sus2</sup>",
  "sus4": "<sup>sus4</sup>",
  "no3d": "(no3)",
  // Seventh
  "maj7": "Δ<sup>7</sup>",
  "augmaj7": "+<sup>Δ7</sup>",
  "min7": "-<sup>7</sup>",
  "minmaj7": "-<sup>Δ7</sup>",
  "7": "<sup>7</sup>",
  "7sus2": "<sup>7sus2</sup>",
  "7sus4": "<sup>7sus4</sup>",
  "dim7": "ø<sup>7</sup>",
  "dimb7": "°<sup>7</sup>",
  "maj7sus2": "Δ<sup>7sus2</sup>",
  "maj7sus4": "Δ<sup>7sus4</sup>",
  // Triads with tensions
  "add9": "<sup>add9</sup>",
  "add11": "<sup>add11</sup>",
  "add13": "<sup>6</sup>",
  "minadd9": "-<sup>add9</sup>",
  "minadd11": "-<sup>add11</sup>",
  "minadd13": "-<sup>6</sup>",
  "dimadd11": "ø<sup>add11</sup>",
  "dimadd13": "ø<sup>6</sup>",
  // Ninths
  "9": "<sup>9</sup>",
  "7b9": "<sup>7(♭9)</sup>",
  "b7b9": "<sup>6(♭9)</sup>",
  "b9": "<sup>7(♭9)</sup>",
  "min9": "-<sup>9</sup>",
  "minb9": "-<sup>7(♭9)</sup>",
  "minmaj9": "-<sup>Δ9</sup>",
  "dim9": "ø<sup>9</sup>",
  "dimb9": "ø<sup>7(♭9)</sup>",
  "majs9": "Δ<sup>7(♯9)</sup>",
  "maj9": "Δ<sup>9</sup>",
  "augmaj9": "+<sup>Δ9</sup>",
  // Elevenths
  "11": "<sup>11</sup>",
  "11b9": "<sup>11(♭9)</sup>",
  "11s": "<sup>♯11</sup>",
  "maj11": "Δ<sup>11</sup>",
  "min11": "-<sup>11</sup>",
  "minmaj11": "-<sup>Δ11</sup>",
  "augmaj11": "+<sup>Δ11</sup>",
  "dim11": "ø<sup>11</sup>",
  "dim11b9": "ø<sup>11(♭9)</sup>",
  "b11b9": "<sup>♭11(♭9)</sup>",
  "maj911s": "Δ<sup>9(♯11)</sup>",
  "majs911s": "Δ<sup>7(♯9♯11)</sup>",
  // Thirteenths
  "13": "<sup>13</sup>",
  "13b": "<sup>♭13</sup>",
  "13b9": "<sup>13(♭9)</sup>",
  "dim13b9": "ø<sup>13(♭9)</sup>",
  "maj13": "Δ<sup>13</sup>",
  "maj1311s": "Δ<sup>13(♯11)</sup>",
  "min1113b": "-<sup>11(♭13)</sup>",
  "min13": "-<sup>13</sup>",
  "minmaj13": "-<sup>Δ13</sup>",
};

const _chordCache: Map<string, Chord> = new Map();
function parseChord(symbol: string): Chord {
  if (_chordCache.has(symbol)) {
    return _chordCache.get(symbol)!;
  }

  let root: Note;
  let third: Note | undefined;
  let fifth: Note | undefined;
  let seventh: Note | undefined;
  let ninth: Note | undefined;
  let eleventh: Note | undefined;
  let thirteenth: Note | undefined;
  let altRoot: Note | undefined;
  let accidental: Accidental | 'random' = 'random';
  let originalSymbol = symbol;

  const ambiguousFlats = [
    "b9", "b79", "b7b9", "b711b9", "b11b9", "b713b9",
  ];

  let quality = symbol;

  // Split quality by '/' for altRoot handling
  if (quality.includes('/')) {
    const [lhs, rhs] = quality.split('/');
    quality = lhs;
    try {
      altRoot = Note.fromString(rhs);
      if (rhs[rhs.length - 1] === 'b') {
        accidental = 'b';
      }
      else if (rhs[rhs.length - 1] === 's') {
        accidental = '#';
      }
    } catch (e) {
      //console.error(`Invalid altRoot note: ${rhs}`);
      altRoot = undefined;
    }
  }

  // Extract root note
  root = Note.fromString(quality[0]);
  quality = quality.slice(1);

  // Handle 'b' flat accidentals and non-ambiguous flats
  if (quality.length > 0 && quality[0] === 'b' && !ambiguousFlats.includes(quality)) {
    root = root.transpose(11);  // Apply flat to root
    quality = quality.slice(1);  // Remove 'b' flat quality
    accidental = 'b';
  }

  // Handle 's' for sharp accidentals (sus2 and sus4)
  if (quality.length > 0 && quality[0] === 's' && !(quality.length > 1 && quality[1] === 'u')) {
    root = root.transpose(1);  // Apply sharp to root
    quality = quality.slice(1);  // Remove 's'
    accidental = '#';
  }
  root = root.norm();

  // Get the chord intervals from the chord_qualities map
  let intervals: (number | undefined)[] = chordIntervals[quality];

  // Apply the root to all intervals, with modulo 12 (to wrap around the chromatic scale)
  [third, fifth, seventh, ninth, eleventh, thirteenth] = intervals.map((interval) => {
    return interval !== undefined ? root.transpose(interval).norm() : undefined;
  });

  // Create the chord object
  const chord = new Chord(
        root,
        third,
        fifth,
        seventh,
        ninth,
        eleventh,
        thirteenth,
        altRoot,
        originalSymbol,
        quality,
        accidental);

  // Cache the result
  _chordCache.set(symbol, chord);

  return chord;
}

export class Chord {
  constructor(
    public root: Note,
    public third?: Note,
    public fifth?: Note,
    public seventh?: Note,
    public ninth?: Note,
    public eleventh?: Note,
    public thirteenth?: Note,
    public altRoot?: Note,
    public originalSymbol: string = '',
    private quality: string = '',
    private accidental: Accidental | 'random' = 'random',
  ) {}

  static fromString(chordStr: string): Chord {
    return parseChord(chordStr);
  }

  toString(chordNotationType: ChordNotationType = 'full'): string {
    const symbolMap = chordNotationType === 'symbols' ? chordSymbolsShort : chordSymbols;
    const symbol = symbolMap[this.quality] || '';
    
    let chordStr = this.root.toString(false, this.accidental) + symbol;
    if (this.altRoot) {
      chordStr += '/' + this.altRoot.toString(false, this.accidental);
    }
    return chordStr;
  }

  private _notesCache: NoteSet | null = null;
  get notes(): NoteSet {
    if (this._notesCache) {
      return this._notesCache;
    }
    const notes = new NoteSet();
    notes.add(this.root);
    if (this.third) notes.add(this.third);
    if (this.fifth) notes.add(this.fifth);
    if (this.seventh) notes.add(this.seventh);
    if (this.ninth) notes.add(this.ninth);
    if (this.eleventh) notes.add(this.eleventh);
    if (this.thirteenth) notes.add(this.thirteenth);
    if (this.altRoot) notes.add(this.altRoot);

    // Cache the notes for future use
    this._notesCache = notes;
    return notes;
  }

  countUniqueNotes(): number {
    return this.notes.size;
  }
}

